/**
 * Firebase configuration management
 * Centralizes and standardizes config management across environments
 */

// Get environment variables with fallbacks
export function getFirebaseConfig() {
  // Log environment variables to help debug

  const config = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  };

  // Validate configuration
  validateConfig(config);

  return config;
}

// Validate the Firebase configuration
function validateConfig(config) {
  const requiredFields = ["apiKey", "authDomain", "projectId"];
  const missingFields = requiredFields.filter((field) => !config[field]);

  if (missingFields.length > 0) {
    const errorMessage = `Firebase configuration is missing required fields: ${missingFields.join(
      ", "
    )}. Check your environment variables.`;
    console.error(errorMessage);

    if (import.meta.env.MODE !== "production") {
      // Only throw in non-production to avoid breaking in production
      throw new Error(errorMessage);
    }
  }
}

// Get base URL for cloud functions
export function getFunctionsBaseUrl() {
  // When running locally or when emulator usage is enabled, connect directly to the functions emulator
  if (shouldUseEmulators()) {
    const emulatorHost = "http://localhost:5001";
    const projectId =
      import.meta.env.VITE_FIREBASE_PROJECT_ID ||
      import.meta.env.FIREBASE_PROJECT_ID;
    if (!projectId) {
      console.error(
        "Firebase project ID not found in environment variables. Cannot connect to functions emulator."
      );
      throw new Error(
        "Firebase project ID is required for emulator connection."
      );
    }
    return `${emulatorHost}/${projectId}/us-central1`;
  }

  // Otherwise, call Cloud Functions directly (stock endpoint)
  const projectId =
    import.meta.env.VITE_FIREBASE_PROJECT_ID ||
    import.meta.env.FIREBASE_PROJECT_ID;
  if (!projectId) {
    console.error("Firebase project ID not found in environment variables.");
    throw new Error("Firebase project ID is required for functions URL.");
  }
  return `https://us-central1-${projectId}.cloudfunctions.net`;
}

// Check if emulators should be used
export function shouldUseEmulators() {
  const isDev = import.meta.env.DEV === true || import.meta.env.MODE === "dev";
  const isEmulatorMode = import.meta.env.MODE === "emulator";
  const useFlag =
    import.meta.env.MODE === "emulator" &&
    String(import.meta.env.VITE_USE_EMULATORS || "").toLowerCase() === "true";

  // Treat Firebase Hosting emulator (localhost:5000) as emulator environment too
  const isHostingEmulator =
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1):5000$/.test(window.location.host);

  return useFlag || isEmulatorMode || isDev || isHostingEmulator;
}

// Get emulator configuration
export function getEmulatorConfig() {
  return {
    auth: { host: "localhost", port: 9099 },
    firestore: { host: "localhost", port: 8080 },
    functions: { host: "localhost", port: 5001 },
  };
}
